import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Flex, Modal, Radio, Space, Spin, Tag } from 'antd'
import { useParams } from 'react-router'
import PatternIcon from '../../../../../../../../../../components/common/PatternIcon'
import {
    useApproveAllQuestionAnswersByCategoryMutation,
    useFetchAnswerCategoryMismatchPredictionsQuery,
    useFetchQuestionAnswersQuery,
    useReviewQuestionAnswerMutation,
    useUpdateQuestionAnswerEscalatedMutation,
} from '../../../../../../../../../../store/apis/submission.api'
import Text from 'antd/es/typography/Text'
import { useDispatch, useSelector } from 'react-redux'
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    FileDoneOutlined,
    LikeOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import textSubmissionDrawerSelectors from '../../../../../../../../../../store/selectors/text-submission-drawer.selectors'
import {
    useSelectNextEscalatedQuestion,
    useTextSubmissionNavigation,
} from '../../../../../../../../../../store/apis/utils/hooks/text-submission.hooks'
import { successColor } from '../../../../../../../../../../constants/styles'
import { submissionActions } from '../../../../../../../../../../store/slices/submission.slice'
import { openNotificationWithIcon } from '../../../../../../../../../../utils/notification.helper'
import { isNumber } from 'lodash'
import {
    fetchAnswerCategoryMismatchPredictionsSelectors,
    fetchQuestionAnswersSelectors,
} from '../../../../../../../../../../store/apis/utils/selectors/submission.api.selectors'
import {
    ConfidenceLevels,
    getConfidenceLevelName,
    getConfidenceLevelNoun,
    getConfidenceLevelTagColor,
} from '../../../../../../../../../../constants/confidence-levels'
import assignmentSelectors from '../../../../../../../../../../store/selectors/assignment.selectors'
import { isMba } from '../../../../../../../../../../utils/assignment.helper'
import useQueryParam from '../../../../../../../../../../hooks/useQueryParam'
import { ASSIGNEE_IDS_PARAM, SUBMISSION_SEARCH_PARAM } from '../../../../../../../../../../constants/query-params'
import { useVersionId } from '../../../../../../../../../../utils/parseURL.helper'
import { useAnswerContext } from '../../../AnswerContext'
import { useExplanation } from '../../../../../../../../../../store/apis/utils/hooks/category.hooks'
import { ExplainabilityStatuses } from '../../../../../../../../../../constants/explainability-statuses'
import userSelectors from '../../../../../../../../../../store/selectors/user.selectors'
import MarkdownRenderer from '../../../../../../../../../../components/common/markdown-renderer'
import { truncate } from 'lodash'
const useShortcutSelection = (categories, onChange, onApprove, disabled) => {
    useEffect(() => {
        const handleKeyDown = event => {
            if (disabled) return
            if (event.altKey && event.key >= '1' && event.key <= `${categories?.length}`) {
                const index = parseInt(event.key, 10) - 1
                if (categories?.[index]) {
                    onChange({ target: { value: categories[index].id } })
                }
            }
            if (event.altKey && event.key === 'Enter') onApprove()
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [categories, onChange])
}

const useAutoscrollToSelectedCategory = ({ selectedCategoryId, containerRef }) => {
    const categoryRefs = useRef({})

    useEffect(() => {
        if (selectedCategoryId && categoryRefs.current[selectedCategoryId] && containerRef?.current) {
            const container = containerRef.current
            const categoryElement = categoryRefs.current[selectedCategoryId]

            // Traverse up to the parent element that is a direct child of the container
            const actualChild = categoryElement.closest('.ant-radio-button-wrapper')

            if (!actualChild) return

            // Calculate horizontal scrolling
            const childLeft = actualChild.offsetLeft
            const childRight = childLeft + actualChild.offsetWidth

            // Scroll to bring the element into view within the container
            if (childLeft < container.scrollLeft) {
                container.scrollLeft = childLeft // Scroll left
            } else if (childRight > container.scrollLeft + container.offsetWidth) {
                container.scrollLeft = childRight - container.offsetWidth // Scroll right
            }
        }
    }, [selectedCategoryId, containerRef])

    return categoryRefs
}

const CategorySelect = ({ question, submissionId, answer, showDetails, readonly, calculateApprovalSpeed }) => {
    const assignmentType = useSelector(assignmentSelectors.type)
    const focusedQuestionId = useSelector(textSubmissionDrawerSelectors.focusedQuestionId)
    const dispatch = useDispatch()
    const versionId = useVersionId()
    const { token, layerId: questionIdParam } = useParams()
    const searchParam = useQueryParam(SUBMISSION_SEARCH_PARAM)
    const questionId = ['all', 'dashboard'].includes(questionIdParam) ? null : parseInt(questionIdParam)
    const userId = useSelector(userSelectors.id)
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const assigneeIdsParam = useQueryParam(ASSIGNEE_IDS_PARAM)

    const [selectedCategoryId, setSelectedCategoryId] = useState(answer.categoryId)
    const { categoryIdToHighlight, setCategoryIdToHighlight } = useAnswerContext()

    // Update selectedCategoryId when categoryIdToHighlight changes
    useEffect(() => {
        if (categoryIdToHighlight) {
            setSelectedCategoryId(categoryIdToHighlight)
        }
    }, [categoryIdToHighlight])

    const containerRef = useRef(null) // Ref for the category container
    const categoryRefs = useAutoscrollToSelectedCategory({ selectedCategoryId, containerRef })
    const [reviewQuestionAnswer, reviewQuestionAnswerResult] = useReviewQuestionAnswerMutation()
    const [approveAllQuestionAnswersByCategory, approveAllQuestionAnswersByCategoryResult] =
        useApproveAllQuestionAnswersByCategoryMutation()
    const { data: answerCategoryMismatchPrediction } = useFetchAnswerCategoryMismatchPredictionsQuery(
        { questionId },
        {
            skip: !isNumber(questionId) || isMba(assignmentType),
            selectFromResult: result =>
                fetchAnswerCategoryMismatchPredictionsSelectors.byAnswerId({ answerId: answer.id }, result),
        },
    )

    const { data: questionSubmissions } = useFetchQuestionAnswersQuery(
        { id: questionId, search: searchParam, versionId, assigneeIds: isOwner ? undefined : [userId] },
        { skip: !isNumber(questionId) },
    )

    const { explanation, isFetching: isFetchingExplanation } = useExplanation(answer)

    const { navigateToNextUnreviewedAnswer } = useTextSubmissionNavigation()

    const getExplanationData = () => {
        if (!explanation) {
            return {
                reasoning: answer.reasoning,
                explainabilityReasoning: answer.explainabilityReasoning,
                highlights: answer.highlights,
                explainabilityStatus: answer.escalated,
                isAdequate: answer.isAdequate,
                ethicalCheckOutcome: answer.ethicalCheckOutcome,
            }
        }

        const isAdequate = explanation.isAdequate
        return {
            reasoning: isAdequate ? explanation.reasoning : null,
            explainabilityReasoning: isAdequate ? null : explanation.reasoning,
            highlights: explanation.highlights,
            explainabilityStatus: isAdequate
                ? ExplainabilityStatuses.UNEXPLAINABLE_RESOLVED
                : ExplainabilityStatuses.UNEXPLAINABLE_UNRESOLVED,
            isAdequate: explanation.isAdequate,
            ethicalCheckOutcome: explanation.ethicalCheckOutcome,
        }
    }

    const onApprove = async event => {
        if (event) {
            event.stopPropagation() // needed to prevent GradingCard's onClick
            event.preventDefault()
        }

        const explanationData = getExplanationData()

        reviewQuestionAnswer({
            versionId,
            id: answer.id,
            categoryId: selectedCategoryId,
            submissionId,
            questionId: answer.questionId,
            search: searchParam,
            token,
            assignmentType,
            mismatchId: answerCategoryMismatchPrediction?.id,
            ...explanationData,
            assigneeIds: assigneeIdsParam?.split(',')?.map(Number),
            approvalSpeedMs: calculateApprovalSpeed ? calculateApprovalSpeed() : null,
        })
        navigateToNextUnreviewedAnswer({ lastReviewedQuestionId: answer.questionId })
    }

    const approveAll = async categoryId => {
        const response = await approveAllQuestionAnswersByCategory({ categoryId })
        if ('data' in response) {
            dispatch(submissionActions.closeSubmission())
            openNotificationWithIcon('success', 'All answers with this category have been approved')
        }
    }

    const [updateQuestionAnswerEscalated] = useUpdateQuestionAnswerEscalatedMutation()

    const search = useQueryParam(SUBMISSION_SEARCH_PARAM)

    const goToNext = useSelectNextEscalatedQuestion(answer.id, submissionId)

    const onResolveUnexplainable = async () => {
        await updateQuestionAnswerEscalated({
            id: answer.id,
            questionId,
            submissionId,
            escalated: ExplainabilityStatuses.UNEXPLAINABLE_RESOLVED,
            search,
            versionId,
            token,
        })

        goToNext()
    }

    const options = question?.categories?.map(category => {
        const isSelectedCategory = selectedCategoryId === category.id
        const buttonType = focusedQuestionId === answer.questionId ? 'primary' : 'default'
        
        const approveButton = isSelectedCategory && !readonly && (
            answer.approveStatus && answer.categoryId === selectedCategoryId ? (
                !answer.isAdequate && answer.escalated !== ExplainabilityStatuses.UNEXPLAINABLE_RESOLVED ? (
                    <Button
                        style={{ backgroundColor: successColor }}
                        type={buttonType}
                        size='large'
                        block
                        onClick={onResolveUnexplainable}
                        icon={<LikeOutlined />}
                        title='Alt + Enter: Approve rubric as compatible'
                        className='mt-8'
                    >
                        Approve as Compatible
                    </Button>
                ) : (
                    <div className='mt-8 text-center' style={{ color: successColor, fontSize: '16px' }}>
                        <LikeOutlined title='Approved' style={{ marginRight: '8px' }} />
                        <Text strong style={{ color: successColor }}>Approved</Text>
                    </div>
                )
            ) : (
                <Button 
                    type={buttonType}
                    size='large'
                    block
                    onClick={onApprove} 
                    icon={<LikeOutlined />} 
                    title='Alt + Enter: Approve this answer'
                    className='mt-8'
                >
                    Approve
                </Button>
            )
        )

        const handleApproveAllClick = () => {
            const { data: numberOfHighConfidenceAnswersPerCategory } =
                fetchQuestionAnswersSelectors.numberOfHighConfidenceAnswersPerCategory(
                    { categoryId: category.id },
                    { data: questionSubmissions },
                )
            Modal.confirm({
                title: 'Confirm approve all',
                icon: <ExclamationCircleOutlined />,
                content: `Are you sure you want to approve all ${
                    isNumber(numberOfHighConfidenceAnswersPerCategory) ? numberOfHighConfidenceAnswersPerCategory : ''
                } AI-predicted answers classified with ${getConfidenceLevelNoun(
                    ConfidenceLevels.VERY_CONFIDENT,
                ).toLowerCase()} in the category ${category.name}?`,
                okText: 'Approve All',
                onOk() {
                    approveAll(category.id)
                },
                cancelText: 'Cancel',
            })
        }

        // Determine if the current category is the AI-recommended category
        const isRecommendedCategory = answerCategoryMismatchPrediction
            ? category.id === answerCategoryMismatchPrediction.predictedCategoryId
            : category.id === answer.categoryId

        // Show approveAll button only when on highest confidence prediction and the recommended category is selected
        const approveAllStatusElement = answer.confidence === ConfidenceLevels.VERY_CONFIDENT &&
            questionId === answer.questionId &&
            !answer.approveStatus &&
            !readonly &&
            isRecommendedCategory &&
            selectedCategoryId === category.id && (
                <Button
                    type={buttonType}
                    size='small'
                    onClick={handleApproveAllClick}
                    icon={<FileDoneOutlined />}
                    title='Approve all AI-predicted answers for this category'
                    loading={approveAllQuestionAnswersByCategoryResult?.isLoading}
                />
            )

        const categoryProperties = []
        if (answer.categoryId === category.id && answer.confidence) {
            categoryProperties.push(
                <Tag
                    style={{ whiteSpace: 'nowrap' }}
                    key='confidence'
                    color={getConfidenceLevelTagColor(answer.confidence)}
                    bordered={false}
                    className='m-0'
                >
                    {getConfidenceLevelName(answer.confidence)}
                </Tag>,
            )
        }

        if (answerCategoryMismatchPrediction?.currentCategoryId === category.id) {
            categoryProperties.push(
                <Tag color='orange' icon={<WarningOutlined />} className='m-0' key='mismatch' bordered={false}>
                    Historical
                </Tag>,
            )
        }

        if (answerCategoryMismatchPrediction?.predictedCategoryId === category.id) {
            categoryProperties.push(
                <Tag color='success' icon={<CheckCircleOutlined />} className='m-0' key='recommended' bordered={false}>
                    Recommended
                </Tag>,
            )
        }

        let mayBeInadequateTag

        if (
            explanation
                ? explanation?.categoryId === category.id && explanation?.isAdequate === false
                : selectedCategoryId === category.id &&
                  answer.categoryId === category.id &&
                  answer.isAdequate === false &&
                  answer.explainabilityReasoning
        ) {
            mayBeInadequateTag = (
                <Tag
                    color='warning'
                    className='m-0 mt-8'
                    key='explanation'
                    bordered={false}
                    style={{
                        whiteSpace: 'wrap',
                    }}
                >
                    <b>Rubric incompatibility?</b>
                    <br />
                    {explanation?.reasoning || answer.explainabilityReasoning}
                </Tag>
            )
        }

        const style =
            answer.approveStatus > 0 && answer.categoryId === category.id && selectedCategoryId === category.id
                ? {
                      border: `1px solid ${successColor}`,
                      backgroundColor: 'rgba(0, 255, 0, 0.05)', // Slightly noticeable green background
                      boxShadow: '0 4px 8px rgba(0, 128, 0, 0.3)', // Green shadow
                      transition: 'all 0.3s ease', // Smooth transition
                  }
                : undefined

        return {
            value: category.id,
            label: (
                <Spin
                    spinning={category.id === selectedCategoryId && isFetchingExplanation}
                    tip='Validating...'
                    wrapperClassName='f-height'
                >
                    <Flex
                        vertical
                        gap={4}
                        justify='space-between'
                        className='f-height'
                        style={{ lineHeight: '1rem' }}
                        ref={el => (categoryRefs.current[category.id] = el)} // Assign ref for each category
                    >
                        <div>
                            <Text>
                                <PatternIcon
                                    id={category.id}
                                    content={category.grade}
                                    colorId={category.colorId}
                                    suffix='pt'
                                />{' '}
                                {category.name}
                            </Text>
                            {showDetails && (
                                <div
                                    className='category-description'
                                    title={category.description?.length > 270 ? category.description : undefined}
                                >
                                    <MarkdownRenderer
                                        content={truncate(category.description, { length: 270 })}
                                        type='secondary'
                                    />
                                </div>
                            )}
                            {mayBeInadequateTag}
                        </div>
                        <Flex justify='space-between' align='flex-end' gap={4} wrap='wrap'>
                            <Space wrap style={{ flex: 1 }}>
                                {categoryProperties}
                            </Space>
                            <Space style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                {selectedCategoryId === category.id && approveAllStatusElement}
                            </Space>
                        </Flex>
                        {/* Full-width approve button at the bottom of selected category */}
                        {approveButton}
                    </Flex>
                </Spin>
            ),
            style: {
                width: `calc(100% / ${question.categories.length})`,
                minWidth: 320,
                height: 'auto',
                padding: 8,
                flexShrink: 0,
                ...style,
            },
        }
    })
    const onChange = event => {
        if (readonly) return
        if (event.target.value === selectedCategoryId) onApprove()
        setSelectedCategoryId(event.target.value)
        setCategoryIdToHighlight(event.target.value)
    }

    useShortcutSelection(question?.categories, onChange, onApprove, focusedQuestionId !== answer.questionId || readonly)

    return (
        <>
            <Radio.Group
                value={selectedCategoryId}
                options={options}
                onChange={onChange}
                ref={containerRef}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    width: `100%`,
                    overflowX: 'auto',
                    borderRadius: 0,
                }}
                optionType='button'
                className='category-select'
            />
            {reviewQuestionAnswerResult.isError && (
                <Alert className='mt-8' message={reviewQuestionAnswerResult?.error} type='error' showIcon closable />
            )}
        </>
    )
}

export default CategorySelect
